import IntelligentContentParser from './IntelligentContentParser';

const STORAGE_PREFIX = 'smartPasteConflictMemory';
const MAX_STORED_DECISIONS = 200;

const normalizeName = (value) => IntelligentContentParser.normalizeParsedEntryName(value);

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (error) {
    return null;
  }
  return null;
};

export const buildConflictPreferenceStorageKey = (characterId) => (
  `${STORAGE_PREFIX}:${String(characterId || 'global')}`
);

export const buildConflictPreferenceKeys = (field, item, conflicts = []) => {
  if (!field || !item || typeof item !== 'object') {
    return { exactKey: null, nameKey: null, primaryMatchType: null };
  }

  const primaryConflict = Array.isArray(conflicts) && conflicts.length > 0 ? conflicts[0] : null;
  if (!primaryConflict) {
    return { exactKey: null, nameKey: null, primaryMatchType: null };
  }

  const fingerprint = IntelligentContentParser.buildParsedEntryFingerprint(item, field);
  const normalizedName = normalizeName(item?.name || '');
  const exactKey = fingerprint ? `exact|${field}|${fingerprint}` : null;
  const nameKey = normalizedName ? `name|${field}|${normalizedName}` : null;

  return {
    exactKey,
    nameKey,
    primaryMatchType: primaryConflict.matchType || 'sameName'
  };
};

export const getRememberedConflictPreference = (preferences, field, item, conflicts = []) => {
  if (!preferences || typeof preferences !== 'object') return null;

  const { exactKey, nameKey, primaryMatchType } = buildConflictPreferenceKeys(field, item, conflicts);
  const exactPreference = exactKey ? preferences[exactKey] : null;
  if (exactPreference && typeof exactPreference === 'object') return exactPreference;
  if (exactPreference) return { resolution: exactPreference };

  if (primaryMatchType === 'sameName' && nameKey) {
    const namePreference = preferences[nameKey];
    if (namePreference && typeof namePreference === 'object') return namePreference;
    if (namePreference) return { resolution: namePreference };
  }

  return null;
};

export const getRememberedConflictResolution = (preferences, field, item, conflicts = []) => {
  return getRememberedConflictPreference(preferences, field, item, conflicts)?.resolution || null;
};

export const getRememberedConflictTarget = (preferences, field, item, conflicts = []) => {
  const preference = getRememberedConflictPreference(preferences, field, item, conflicts);
  if (!preference) return null;

  return {
    targetFingerprint: preference.targetFingerprint || null,
    targetName: preference.targetName || null
  };
};

const pruneConflictPreferences = (preferences) => {
  const entries = Object.entries(preferences || {});
  if (entries.length <= MAX_STORED_DECISIONS) return preferences || {};

  return entries
    .sort((left, right) => {
      const leftTime = Date.parse(left[1]?.updatedAt || '') || 0;
      const rightTime = Date.parse(right[1]?.updatedAt || '') || 0;
      return rightTime - leftTime;
    })
    .slice(0, MAX_STORED_DECISIONS)
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
};

export const recordConflictResolution = (preferences, { field, item, conflicts, resolution, targetFingerprint, targetName } = {}) => {
  if (!field || !item || !resolution) return preferences || {};

  const { exactKey, nameKey, primaryMatchType } = buildConflictPreferenceKeys(field, item, conflicts);
  const next = { ...(preferences || {}) };
  const payload = {
    resolution,
    matchType: primaryMatchType,
    targetFingerprint: targetFingerprint || undefined,
    targetName: targetName || undefined,
    updatedAt: new Date().toISOString()
  };

  if (primaryMatchType === 'exact' && exactKey) {
    next[exactKey] = payload;
  } else if (nameKey) {
    next[nameKey] = payload;
  }

  return pruneConflictPreferences(next);
};

export const recordConflictResolutions = (preferences, decisions = []) => (
  (Array.isArray(decisions) ? decisions : []).reduce(
    (acc, decision) => recordConflictResolution(acc, decision),
    preferences || {}
  )
);

export const loadConflictPreferenceStore = (characterId) => {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(buildConflictPreferenceStorageKey(characterId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const saveConflictPreferenceStore = (characterId, preferences) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(
      buildConflictPreferenceStorageKey(characterId),
      JSON.stringify(pruneConflictPreferences(preferences || {}))
    );
  } catch (error) {
    // Ignore storage quota / availability issues; SmartPaste still works without persistence.
  }
};

const SmartPasteConflictMemory = {
  buildConflictPreferenceStorageKey,
  buildConflictPreferenceKeys,
  getRememberedConflictPreference,
  getRememberedConflictResolution,
  getRememberedConflictTarget,
  recordConflictResolution,
  recordConflictResolutions,
  loadConflictPreferenceStore,
  saveConflictPreferenceStore
};

export default SmartPasteConflictMemory;
